/**
 * THE LIVE WALL. You are looking out of a cockpit.
 *
 * The canopy plate frames the screen, the sky, planet and core are outside the glass, the ships
 * cross the window, MO stands inside as a hologram and looks at whoever moves the pointer, and the
 * instruments sit on the console below the sill. It is the room view: the one a visitor sees, the
 * one a screen left alone turns into. Internal surface, so the franchise skin applies in full.
 *
 * EVERYTHING COUNTED IS REAL. Motion is theatre; numbers are the payload. A lamp flashes only when
 * a lane reports (useAssurance), a bar is a day's posts (publishing), a gauge is days since an
 * objective moved (intent.staleDays). Nothing here is simulated, and when the server does not
 * carry a figure the panel says so rather than drawing one.
 *
 * Plates are alpha, every one. The five that shipped opaque were baked (ops/plates/bake-alpha.py),
 * so nothing in liveWall.css needs `mix-blend-mode`, and the test on that file keeps it so.
 */
import { Suspense, lazy, useEffect, useMemo, useRef, useState, type CSSProperties, type JSX } from 'react';
import type { AssuranceView, Dashboard, DashboardCommitment, DashboardKeyAction, DashboardMeeting, DashboardObjective, DashboardUpdate } from '@rbv/shared';
import { Aurebesh } from './Aurebesh.js';
import { CREW_FALLBACK, ViewPicker, isLive } from './CommandDeck.js';
/*
 * LAZY, so three.js stays in its own chunk. Bundled into the main entry it pushed the entry past
 * the service worker's precache limit and the image build failed in the PWA step, after Vite had
 * already printed its "built" line. The wall does not need the renderer to draw anything else.
 */
const MoFigure3D = lazy(() => import('./MoFigure3D.js').then((m) => ({ default: m.MoFigure3D })));
import { usePointerTilt } from './pointerTilt.js';
import { useAssurance } from './useAssurance.js';
import type { DeckView } from './views.js';
import './liveWall.css';

const FLASH_MS = 6000;

function useNow(everyMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

const pad = (n: number) => String(n).padStart(2, '0');

function countdown(to: Date, now: Date): string {
  const ms = to.getTime() - now.getTime();
  if (!Number.isFinite(ms)) return '';
  if (ms <= 0) return 'now';
  const h = Math.floor(ms / 3.6e6);
  const m = Math.floor((ms % 3.6e6) / 6e4);
  return h > 0 ? `${h}h ${pad(m)}m` : `${m}m`;
}

function ago(iso: string | null | undefined, now: Date): string {
  if (!iso) return 'never';
  const ms = now.getTime() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'now';
  const m = Math.round(ms / 6e4);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

/** The next timed meeting still ahead, else the first on the agenda, else none. */
function nextMeeting(agenda: DashboardMeeting[], now: Date): DashboardMeeting | null {
  return (
    agenda.find((m) => !m.allDay && new Date(m.startsAt).getTime() > now.getTime()) ?? agenda[0] ?? null
  );
}

const cssVars = (v: Record<string, number | string>): CSSProperties => v as CSSProperties;

/*
 * THE WALL NAMES NO CLIENT. It is the room view, the one a visitor sees, so a meeting whose note is
 * `client-confidential` shows as a client meeting with no title and no guest. The work views keep
 * the title; the redaction is about where this surface is looked at, not about the data.
 */
const shownTitle = (m: DashboardMeeting): string => (m.confidential ? 'Client meeting' : m.title);
const shownWho = (m: DashboardMeeting): string => (m.confidential ? 'confidential' : m.organiser);

export function LiveWall({
  dashboard: d,
  onPick,
  screensaver = false,
  onWake,
}: {
  dashboard: Dashboard;
  onPick: (v: DeckView) => void;
  /** Overlaying another view after idle. The first input dismisses it and lands nowhere else. */
  screensaver?: boolean;
  onWake?: () => void;
}): JSX.Element {
  const now = useNow(1000);
  const assurance = useAssurance();
  const stage = useRef<HTMLDivElement>(null);
  usePointerTilt(stage);
  const root = useRef<HTMLElement>(null);

  const next = nextMeeting(d.agenda, now);
  const nextAt = next ? new Date(next.startsAt) : null;
  const decisions = d.proposals.length + d.proposalsMore;
  const live = d.lanes.due + d.lanes.live;
  const crew = d.telemetry.crew && d.telemetry.crew.length > 0 ? d.telemetry.crew : CREW_FALLBACK;
  const active = crew.filter((c) => isLive(c.state)).length;
  const main = d.intent[0] ?? null;
  const pub = d.publishing;
  const lit = useLitLamps(assurance);
  /* True once the 3D figure has loaded; the plate and its emblem then step aside. */
  const [solid, setSolid] = useState(false);
  const worst = assurance?.lanes.find((l) => l.state !== 'healthy') ?? null;

  const fullscreen = () => {
    const el = root.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  };

  return (
    <main
      ref={root}
      className="mo-dash lw"
      data-screensaver={screensaver || undefined}
      onPointerDown={screensaver ? onWake : undefined}
      onKeyDown={screensaver ? onWake : undefined}
      aria-label="Live wall"
    >
      {/* Outside the glass. All alpha plates; nothing here blends. */}
      <div className="lw-sky" aria-hidden="true" />
      <div className="lw-sky lw-sky2" aria-hidden="true" />
      <img className="lw-planet" src="/deck-planet.webp" alt="" aria-hidden="true" />
      <img className="lw-core" src="/deck-core.webp" alt="" aria-hidden="true" />
      <img className="lw-ship" data-lane="far" src="/ship-freighter.webp" alt="" aria-hidden="true" />
      <img className="lw-ship" data-lane="mid" src="/ship-fighter.webp" alt="" aria-hidden="true" />
      <img className="lw-ship" data-lane="near" src="/ship-interceptor.webp" alt="" aria-hidden="true" />
      <img className="lw-frame" src="/deck-frame.webp" alt="" aria-hidden="true" />
      <div className="lw-scan" aria-hidden="true" />
      <div className="lw-sweep" aria-hidden="true" />

      <header className="lw-top">
        <div className="lw-word">
          <img src="/deck-emblem.webp" alt="" aria-hidden="true" />
          <div>
            <p className="lw-wordmark">MO OS</p>
            <p className="lw-wordsub">personal intelligence system</p>
          </div>
        </div>
        <Aurebesh className="lw-crawl" seed="live-wall-crawl" length={44} size={11} />
        <div className="lw-clock">
          <p className="lw-time">{`${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`}</p>
          <p className="lw-date">
            {now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        {!screensaver && (
          <div className="lw-controls">
            <button type="button" className="lw-fs" onClick={fullscreen} title="Full screen">
              <span aria-hidden="true">⤢</span> Full screen
            </button>
            <ViewPicker current="live" onPick={onPick} />
          </div>
        )}
      </header>

      <section className="lw-window">
        {/* HUD dressing: brackets, two data streams, all theatre. */}
        <span className="lw-corner" data-at="tl" aria-hidden="true" />
        <span className="lw-corner" data-at="tr" aria-hidden="true" />
        <span className="lw-corner" data-at="bl" aria-hidden="true" />
        <span className="lw-corner" data-at="br" aria-hidden="true" />
        <div className="lw-stream" data-side="l" aria-hidden="true">
          <Aurebesh seed="live-wall-stream-left" length={60} size={10} />
        </div>
        <div className="lw-stream" data-side="r" aria-hidden="true">
          <Aurebesh seed="live-wall-stream-right" length={60} size={10} />
        </div>

        {/* What is moving: one key action, one reminder, one reading, each cycling through its list. */}
        <Highlights dashboard={d} now={now} />

        {/* The master slider: three real states, cycling on a CSS clock. */}
        <div className="lw-slider">
          <div className="lw-slide">
            <p className="lw-k">MO OS</p>
            <p className="lw-h">I monitor, connect and remind.</p>
            <p className="lw-s">You decide, I execute.</p>
          </div>
          <div className="lw-slide">
            <p className="lw-k">
              {next && nextAt
                ? `Next up · ${nextAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                : 'Next up'}
            </p>
            {next && nextAt ? (
              <>
                <p className="lw-big">{countdown(nextAt, now)}</p>
                <p className="lw-h lw-h-sm">{shownTitle(next)}</p>
                <p className="lw-s">{shownWho(next)}</p>
              </>
            ) : (
              <p className="lw-h lw-h-sm">Nothing on the calendar</p>
            )}
          </div>
          <div className="lw-slide">
            <p className="lw-k">{main ? `Main objective · ${main.horizon}` : 'Objectives'}</p>
            <p className="lw-h lw-h-sm">{main ? main.name : 'None declared'}</p>
            {main && (
              <p className="lw-s">
                {main.engagements.length} engagements · {main.openCommitments} open ·{' '}
                {main.staleDays === null ? 'no movement dated' : `moved ${main.staleDays}d ago`}
              </p>
            )}
          </div>
          <div className="lw-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
        </div>

        <div className="lw-stage" ref={stage}>
          <div className="lw-floor" aria-hidden="true" />
          <div className="lw-halo" aria-hidden="true" />
          <Reticle />
          <div className="lw-ring" aria-hidden="true" />
          <img className="lw-droid" src="/char-droid.webp" alt="" aria-hidden="true" />
          <img className="lw-buddy" src="/char-companion.webp" alt="" aria-hidden="true" />
          {/* The plate stands until the model has loaded, and stays if it never does. */}
          <div className="lw-figure" data-solid={solid || undefined}>
            <img className="lw-mo" src="/deck-mo.webp" alt="MO" width={460} height={460} />
            <img className="lw-emblem" src="/deck-emblem.webp" alt="" aria-hidden="true" />
            <Suspense fallback={null}>
              <MoFigure3D onReady={setSolid} />
            </Suspense>
          </div>
          <p className="lw-name">MO</p>
        </div>

        <div className="lw-tiles">
          <div className="lw-holo">
            <img src="/deck-holo.webp" alt="" aria-hidden="true" />
            <div>
              <p className="lw-v lw-v-sm">{d.holdings.totalNotes.toLocaleString()}</p>
              <p className="lw-kk">
                notes · {(d.holdings.counts.person ?? 0).toLocaleString()} people ·{' '}
                {(d.holdings.counts.org ?? 0).toLocaleString()} accounts
              </p>
            </div>
          </div>
          <Tile ring="◉" value={String(decisions)} label="decisions need your judgement" />
          {/* The two triage lanes, by their own names. `due` carries a date; `live` was agreed recently. */}
          <Tile
            ring="◎"
            tone="count"
            value={String(live)}
            label={
              <>
                actions in play · <b>{d.lanes.due}</b> due · <b>{d.lanes.overdue}</b> overdue
              </>
            }
          />
          <Tile
            ring="◷"
            tone="time"
            value={nextAt ? countdown(nextAt, now) : '—'}
            label={
              next ? (
                <>
                  until <b>{shownTitle(next)}</b>
                </>
              ) : (
                'nothing scheduled'
              )
            }
          />
        </div>
      </section>

      <section className="lw-console">
        <div className="lw-panel">
          <p className="lw-lbl">
            Lane telemetry
            <span>
              {assurance
                ? `${assurance.lanes.length} lanes · ${assurance.lanes.filter((l) => l.state === 'healthy').length} holding`
                : 'not reported'}
            </span>
          </p>
          {assurance ? (
            <div className="lw-lanes">
              {assurance.lanes.map((l) => (
                <div key={l.lane} className="lw-lane" data-state={l.state}>
                  <span className="lw-lamp" data-state={l.state} data-lit={lit[l.lane] !== undefined || undefined} />
                  <span className="lw-lane-n">{l.label}</span>
                  <span className="lw-lane-a">{ago(l.last?.at, now)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="lw-none">The assurance route did not answer.</p>
          )}
          {worst && <p className="lw-verdict">{`${worst.label}: ${worst.why}`}</p>}
        </div>

        <div className="lw-panel">
          <p className="lw-lbl">
            Objectives <span>days since movement</span>
          </p>
          {d.intent.length === 0 ? (
            <p className="lw-none">None declared.</p>
          ) : (
            <div className="lw-gauges">
              {d.intent.slice(0, 3).map((o) => (
                <Gauge key={o.path} objective={o} />
              ))}
            </div>
          )}
        </div>

        <div className="lw-panel">
          <p className="lw-lbl">
            Published <span>last 14 days</span>
          </p>
          {pub ? (
            <>
              <div className="lw-bars">
                {pub.days.map((day, i) => (
                  <div key={day.date} className="lw-bar" style={cssVars({ '--v': day.count, '--i': i })}>
                    <span className="lw-bar-v">{day.count || ''}</span>
                    <i />
                    <span className="lw-bar-d">{day.date.slice(8)}</span>
                  </div>
                ))}
              </div>
              <p className="lw-sub">
                <b>{pub.total}</b> filed · <b>{pub.thisWeek}</b> this week
                {pub.latest && (
                  <>
                    {' '}
                    · {pub.latest.title}, {pub.latest.publishedAt.slice(5)}
                  </>
                )}
              </p>
            </>
          ) : (
            <p className="lw-none">This server does not report publishing.</p>
          )}
        </div>

        <div className="lw-panel">
          <p className="lw-lbl">
            The crew <span>{active} active</span>
          </p>
          <div className="lw-crew">
            {crew.map((c) => (
              <div key={c.name} className="lw-cm" data-state={c.state}>
                <div className="lw-face">
                  <img src={`/crew-${c.plate}.webp`} alt="" />
                </div>
                <p className="lw-cn">
                  {c.name}
                  {c.mark && <span className="lw-kanji"> {c.mark}</span>}
                </p>
                <p className="lw-cr">{c.role}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lw-feed">
          <p className="lw-lbl">
            Since you last checked <span>{d.updates.length}</span>
          </p>
          <div className="lw-ticker">
            <div className="lw-run">
              {d.updates.slice(0, 20).map((u) => (
                <span key={u.path + u.at} className="lw-ev">
                  <span className="lw-kind" data-kind={u.kind}>
                    {u.kind}
                  </span>
                  {/* Same rule as the meeting: a change to a confidential account is shown as a change, unnamed. */}
                  {u.confidential ? 'confidential' : u.title}
                  <span className="lw-ago">{ago(u.at, now)}</span>
                </span>
              ))}
              {d.updates.length === 0 && <span className="lw-ev">Nothing has changed since you last looked.</span>}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

/** Concentric targeting rings behind the figure. Decoration; the rotation is in the CSS motion block. */
function Reticle(): JSX.Element {
  const ticks = Array.from({ length: 36 }, (_, i) => i * 10);
  return (
    <svg className="lw-reticle" viewBox="-100 -100 200 200" aria-hidden="true">
      <g className="lw-reticle-a">
        <circle r="92" fill="none" strokeWidth="0.6" strokeDasharray="4 6" />
        {ticks.map((a) => (
          <line key={a} x1="0" y1="-92" x2="0" y2={a % 90 === 0 ? -80 : -87} strokeWidth={a % 90 === 0 ? 1.2 : 0.5} transform={`rotate(${a})`} />
        ))}
      </g>
      <g className="lw-reticle-b">
        <circle r="70" fill="none" strokeWidth="0.5" strokeDasharray="1 3" />
        <path d="M-70 0 L-60 0 M70 0 L60 0 M0 -70 L0 -60 M0 70 L0 60" strokeWidth="1" />
      </g>
      <circle r="48" fill="none" strokeWidth="0.4" opacity="0.5" />
    </svg>
  );
}

const dueWord = (days: number | null): string =>
  days === null ? 'no date stated' : days < 0 ? `overdue ${-days}d` : days === 0 ? 'due today' : `due in ${days}d`;

/**
 * The three cycling cards in the window's upper band: a key action, a reminder, a reading.
 *
 * Each cycles through ITS list on a nine-second clock, so the wall is never showing the same
 * sentence for long and never shows anything that is not on the payload. A flagged item shows as
 * its kind and the word confidential, on the same rule as the meeting and the ticker. With motion
 * reduced the clock does not run and the first of each list stands.
 */
function Highlights({ dashboard: d, now }: { dashboard: Dashboard; now: Date }): JSX.Element {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setTick((n) => n + 1), 9000);
    return () => clearInterval(t);
  }, []);

  const actions: DashboardKeyAction[] = d.keyActions;
  const reminders = useMemo<DashboardCommitment[]>(() => {
    const dated = d.owed.filter((c) => c.dueInDays !== null).sort((a, b) => (a.dueInDays ?? 0) - (b.dueInDays ?? 0));
    return dated.length > 0 ? dated : d.owed;
  }, [d.owed]);
  const readings = useMemo<DashboardUpdate[]>(
    () => d.updates.filter((u) => u.kind === 'thread' || u.kind === 'decision' || u.kind === 'meeting'),
    [d.updates],
  );
  const pick = <T,>(xs: T[]): T | null => (xs.length === 0 ? null : xs[tick % xs.length]!);
  const a = pick(actions);
  const r = pick(reminders);
  const g = pick(readings);
  void now;

  return (
    <div className="lw-hud">
      <div className="lw-card" data-tone="act">
        <p className="lw-card-k">
          Key action <span>{actions.length === 0 ? 'none' : `${(tick % actions.length) + 1} of ${actions.length + d.keyActionsMore}`}</span>
        </p>
        {a ? (
          <>
            <p className="lw-card-t">{a.confidential ? 'A confidential account' : a.text}</p>
            <p className="lw-card-m">
              {a.reason} · {a.mine ? 'yours to do' : 'waiting on them'}
            </p>
          </>
        ) : (
          <p className="lw-card-m">Nothing is due or live right now.</p>
        )}
      </div>
      <div className="lw-card" data-tone={r && r.dueInDays !== null && r.dueInDays < 0 ? 'late' : 'remind'}>
        <p className="lw-card-k">
          Reminder <span>{reminders.length === 0 ? 'none' : `${(tick % reminders.length) + 1} of ${d.owedTotal}`}</span>
        </p>
        {r ? (
          <>
            <p className="lw-card-t">{r.confidential ? 'A confidential account' : r.text}</p>
            <p className="lw-card-m">
              {dueWord(r.dueInDays)}
              {r.counterparty && !r.confidential ? ` · ${r.counterparty}` : ''}
              {r.objective ? ` · serves ${r.objective}` : ''}
            </p>
          </>
        ) : (
          <p className="lw-card-m">You owe nothing that is recorded.</p>
        )}
      </div>
      <div className="lw-card" data-tone="read">
        <p className="lw-card-k">
          Latest {g ? g.kind : 'reading'} <span>{readings.length === 0 ? 'none' : `${(tick % readings.length) + 1} of ${readings.length}`}</span>
        </p>
        {g ? (
          <>
            <p className="lw-card-t">{g.confidential ? 'confidential' : g.title}</p>
            <p className="lw-card-m">{g.at.slice(0, 10)}</p>
          </>
        ) : (
          <p className="lw-card-m">No reading has been filed since you last looked.</p>
        )}
      </div>
    </div>
  );
}

function Tile({
  ring,
  value,
  label,
  tone,
}: {
  ring: string;
  value: string;
  label: JSX.Element | string;
  tone?: 'count' | 'time';
}): JSX.Element {
  return (
    <div className="lw-tile" data-tone={tone}>
      <span className="lw-rg" aria-hidden="true">
        {ring}
      </span>
      <div>
        <p className="lw-v">{value}</p>
        <p className="lw-kk">{label}</p>
      </div>
    </div>
  );
}

/** Arc from fresh (full) to quiet (empty) over thirty days; null stays empty and says so. */
function Gauge({ objective: o }: { objective: DashboardObjective }): JSX.Element {
  const days = o.staleDays;
  const offset = days === null ? 100 : Math.min(100, Math.round((days / 30) * 100));
  const tone = days === null ? 'unknown' : days <= 3 ? 'fresh' : days <= 14 ? 'warm' : 'quiet';
  return (
    <div className="lw-g" data-tone={tone}>
      <svg viewBox="0 0 120 80" aria-hidden="true">
        <path d="M12 68 A48 48 0 0 1 108 68" fill="none" className="lw-g-track" strokeWidth="7" />
        <path
          d="M12 68 A48 48 0 0 1 108 68"
          fill="none"
          className="lw-g-arc"
          strokeWidth="7"
          pathLength="100"
          style={cssVars({ '--o': offset })}
        />
        <text className="lw-g-v" x="60" y="62" textAnchor="middle">
          {days === null ? '—' : `${days}d`}
        </text>
        <text className="lw-g-k" x="60" y="76" textAnchor="middle">
          {days === null ? 'undated' : tone}
        </text>
      </svg>
      <p className="lw-g-n">
        <b>{o.name}</b>
        {o.engagements.length} engagements · {o.openCommitments} open
      </p>
    </div>
  );
}

/** Which lanes reported since the last poll: lit for FLASH_MS. The same rule HealthStrip runs. */
function useLitLamps(view: AssuranceView | null): Record<string, number> {
  const [lit, setLit] = useState<Record<string, number>>({});
  const seen = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!view) return;
    const nowMs = Date.now();
    const fresh: Record<string, number> = {};
    for (const l of view.lanes) {
      const at = l.last?.at ?? '';
      if (at && seen.current[l.lane] !== undefined && seen.current[l.lane] !== at) fresh[l.lane] = nowMs;
      seen.current[l.lane] = at;
    }
    if (Object.keys(fresh).length === 0) return;
    setLit((cur) => ({ ...cur, ...fresh }));
    const t = setTimeout(
      () =>
        setLit((cur) => {
          const next = { ...cur };
          for (const k of Object.keys(fresh)) delete next[k];
          return next;
        }),
      FLASH_MS,
    );
    return () => clearTimeout(t);
  }, [view]);
  return lit;
}
